import {
  CdkMenu,
  CdkMenuGroup,
  CdkMenuItem,
  CdkMenuItemRadio,
  CdkMenuTrigger,
} from '@angular/cdk/menu';
import { ConnectedPosition } from '@angular/cdk/overlay';
import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { IconComponent } from '../icon/icon.component';
import { IconName } from '../icon/icons';

export type MenuEntry =
  | {
      readonly kind: 'item';
      readonly id: string;
      readonly label: string;
      readonly icon?: IconName;
      readonly testId?: string;
    }
  | {
      readonly kind: 'radio';
      readonly id: string;
      readonly label: string;
      readonly checked: boolean;
      readonly icon?: IconName;
      readonly testId?: string;
    }
  | { readonly kind: 'heading'; readonly label: string }
  | { readonly kind: 'separator' };

/** Una sezione del menu: un titolo facoltativo e le voci fino al separatore successivo. */
interface MenuSection {
  readonly heading: string | null;
  readonly entries: readonly Exclude<MenuEntry, { kind: 'heading' } | { kind: 'separator' }>[];
}

const ABOVE: ConnectedPosition[] = [
  { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -8 },
  { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 8 },
];
const BELOW_END: ConnectedPosition[] = [
  { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 8 },
  { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -8 },
];

/**
 * Menu a tendina su CDK Menu: frecce per spostarsi, Invio per scegliere, Esc per chiudere, con
 * il fuoco che torna al pulsante. Il contenuto del pulsante si proietta; le voci sono dati.
 */
@Component({
  selector: 'bs-menu',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CdkMenuTrigger, CdkMenu, CdkMenuGroup, CdkMenuItem, CdkMenuItemRadio, IconComponent],
  host: { class: 'block' },
  template: `
    <button
      type="button"
      [cdkMenuTriggerFor]="panel"
      [cdkMenuPosition]="positions()"
      [class]="triggerClass()"
      [attr.aria-label]="label()"
      [attr.data-testid]="testId()"
    >
      <ng-content />
    </button>
    <ng-template #panel>
      <div
        cdkMenu
        class="flex min-w-56 flex-col rounded-2xl border border-stone-200 bg-white p-1.5 text-sm shadow-lg dark:border-stone-700 dark:bg-stone-900"
      >
        @for (section of sections(); track $index; let first = $first) {
          @if (!first) {
            <div role="separator" class="my-1 h-px bg-stone-200 dark:bg-stone-700"></div>
          }
          <div cdkMenuGroup [attr.aria-label]="section.heading">
            @if (section.heading; as heading) {
              <div
                class="px-3 pb-1 pt-2 text-xs font-semibold text-stone-500 dark:text-stone-400"
                aria-hidden="true"
              >
                {{ heading }}
              </div>
            }
            @for (entry of section.entries; track entry.id) {
              @if (entry.kind === 'radio') {
                <button
                  type="button"
                  cdkMenuItemRadio
                  [cdkMenuItemChecked]="entry.checked"
                  [class]="itemClass"
                  [attr.data-testid]="entry.testId ?? null"
                  (cdkMenuItemTriggered)="selected.emit(entry.id)"
                >
                  @if (entry.icon; as name) {
                    <bs-icon [name]="name" [size]="18" />
                  }
                  <span class="flex-1">{{ entry.label }}</span>
                  @if (entry.checked) {
                    <bs-icon name="check" [size]="18" />
                  }
                </button>
              } @else {
                <button
                  type="button"
                  cdkMenuItem
                  [class]="itemClass"
                  [attr.data-testid]="entry.testId ?? null"
                  (cdkMenuItemTriggered)="selected.emit(entry.id)"
                >
                  @if (entry.icon; as name) {
                    <bs-icon [name]="name" [size]="18" />
                  }
                  <span class="flex-1">{{ entry.label }}</span>
                </button>
              }
            }
          </div>
        }
      </div>
    </ng-template>
  `,
})
export class MenuComponent {
  readonly entries = input.required<readonly MenuEntry[]>();
  /** Nome accessibile del pulsante, se il contenuto proiettato non basta. */
  readonly label = input<string | null>(null);
  /** `block`: pulsante largo con contenuto libero (area utente); `icon`: pulsante tondo. */
  readonly appearance = input<'block' | 'icon'>('icon');
  readonly placement = input<'above' | 'below-end'>('below-end');
  readonly testId = input<string | null>(null);
  readonly selected = output<string>();

  protected readonly itemClass =
    'flex h-11 w-full items-center gap-3 rounded-xl px-3 text-left font-medium text-ink outline-none hover:bg-stone-100 focus:bg-stone-100 dark:text-stone-50 dark:hover:bg-stone-800 dark:focus:bg-stone-800';

  protected readonly positions = computed(() => (this.placement() === 'above' ? ABOVE : BELOW_END));
  protected readonly triggerClass = computed(() =>
    this.appearance() === 'block'
      ? 'flex w-full items-center gap-3 rounded-2xl p-2 text-left hover:bg-stone-200 dark:hover:bg-stone-800'
      : 'inline-flex size-10 items-center justify-center rounded-full text-stone-600 hover:bg-stone-100 dark:text-stone-300 dark:hover:bg-stone-800',
  );

  protected readonly sections = computed<MenuSection[]>(() => {
    const sections: { heading: string | null; entries: MenuSection['entries'][number][] }[] = [];
    let current: (typeof sections)[number] | null = null;
    for (const entry of this.entries()) {
      if (entry.kind === 'separator') {
        current = null;
        continue;
      }
      if (entry.kind === 'heading') {
        current = { heading: entry.label, entries: [] };
        sections.push(current);
        continue;
      }
      if (!current) {
        current = { heading: null, entries: [] };
        sections.push(current);
      }
      current.entries.push(entry);
    }
    return sections;
  });
}
